import { supabase } from '../../config/supabase';

const FINAL_PRICING_DEPARTMENTS = ['roof', 'wall', 'floor'];

const normalizeFinalPricings = (records = []) => {
    const map = { roof: null, wall: null, floor: null };

    records.forEach(record => {
        if (record?.department && FINAL_PRICING_DEPARTMENTS.includes(record.department)) {
            map[record.department] = record;
        }
    });

    return map;
};

const hasAllFinalPricings = pricings =>
    FINAL_PRICING_DEPARTMENTS.every(key => !!pricings[key]);

const hasAnyFinalPricing = pricings =>
    FINAL_PRICING_DEPARTMENTS.some(key => !!pricings[key]);

const sumFinalPricing = pricings =>
    FINAL_PRICING_DEPARTMENTS.reduce(
        (sum, key) => sum + (Number(pricings[key]?.final_price) || 0),
        0
    );

const dayKey = date => new Date(date).toISOString().slice(0, 10);

/**
 * Aggregate stats for the beta admin dashboard. Runs 4 queries in parallel
 * (jobs+relations, users, sent-quotation conversations, recent activity) and
 * does all counting/bucketing client-side over the results — same approach
 * dashboard.js already uses, just with a richer set of source tables so the
 * dashboard can surface pipeline value, pricing/quotation gaps, a job-created
 * trend, and a team activity leaderboard alongside the existing job counts.
 */
export const getDashboardBetaStats = async companyId => {
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const weekFromNow = new Date();
    weekFromNow.setDate(weekFromNow.getDate() + 7);

    const trendStart = new Date();
    trendStart.setDate(trendStart.getDate() - 13);

    const [jobsResult, usersResult, sentResult, activityResult] = await Promise.all([
        supabase
            .from('jobs')
            .select(`
                id, unique_id, customer_name, job_type, main_status,
                created_at, urgent_design, urgent_quote, is_cancelled, on_hold,
                job_quotes(id, quote_type, is_required, design_status, estimation_status,
                           delivery_status, production_status, expected_delivery_date,
                           expected_finish_date, child_unique_id),
                job_final_pricings(department, final_price)
            `)
            .eq('company_id', companyId)
            .eq('deleted', false)
            .order('created_at', { ascending: false }),

        supabase
            .from('users')
            .select('id, full_name, username, role, avatar_url', { count: 'exact' })
            .eq('company_id', companyId),

        supabase
            .from('customer_conversations')
            .select('job_id, metadata')
            .eq('company_id', companyId)
            .eq('conversation_type', 'quotation_email')
            .eq('direction', 'outbound')
            .eq('status', 'sent'),

        supabase
            .from('job_action_logs')
            .select('id, job_id, user_name, user_role, action, details, action_origin, timestamp')
            .eq('company_id', companyId)
            .order('timestamp', { ascending: false })
            .limit(40),
    ]);

    if (jobsResult.error) throw jobsResult.error;
    if (usersResult.error) throw usersResult.error;
    if (sentResult.error) throw sentResult.error;
    if (activityResult.error) throw activityResult.error;

    const jobs = jobsResult.data || [];
    const totalUsers = usersResult.count ?? (usersResult.data || []).length;
    const activity = activityResult.data || [];

    const sentJobIds = new Set(
        (sentResult.data || [])
            .filter(row => (row.metadata?.quoted_departments || []).length > 0)
            .map(row => row.job_id)
    );

    const activeJobs = jobs.filter(job => job.main_status !== 'completed' && !job.is_cancelled);

    const jobsWithPricing = jobs.map(job => {
        const pricings = normalizeFinalPricings(job.job_final_pricings);
        return {
            job,
            fullyPriced: hasAllFinalPricings(pricings),
            anyPriced: hasAnyFinalPricing(pricings),
            value: sumFinalPricing(pricings),
        };
    });

    const pipelineValue = jobsWithPricing
        .filter(({ job }) => !job.is_cancelled)
        .reduce((sum, { value }) => sum + value, 0);

    const fullyPricedJobs = jobsWithPricing.filter(({ fullyPriced }) => fullyPriced).length;

    const missingPricingJobs = jobsWithPricing.filter(
        ({ job, fullyPriced }) => !job.is_cancelled && !fullyPriced
    ).length;

    const notYetQuotedJobs = jobsWithPricing.filter(
        ({ job, anyPriced }) => !job.is_cancelled && anyPriced && !sentJobIds.has(job.id)
    ).length;

    const now = new Date();
    let dueThisWeek = 0;
    jobs.forEach(job => {
        (job.job_quotes || []).forEach(quote => {
            if (!quote.is_required) return;
            const dateValue = quote.expected_delivery_date || quote.expected_finish_date;
            if (!dateValue) return;
            const date = new Date(dateValue);
            if (date >= now && date <= weekFromNow) dueThisWeek += 1;
        });
    });

    const statusBreakdown = [
        { key: 'pending_design', label: 'Pending Design', count: activeJobs.filter(j => j.main_status === 'pending_design').length },
        { key: 'pending_estimation', label: 'Pending Estimate', count: activeJobs.filter(j => j.main_status === 'pending_estimation').length },
        { key: 'in_progress', label: 'In Progress', count: activeJobs.filter(j => j.main_status === 'in_progress').length },
        { key: 'ready_for_delivery', label: 'Ready for Delivery', count: activeJobs.filter(j => j.main_status === 'ready_for_delivery').length },
    ];

    const trendMap = {};
    for (let i = 0; i < 14; i += 1) {
        const d = new Date(trendStart);
        d.setDate(d.getDate() + i);
        trendMap[dayKey(d)] = 0;
    }
    jobs.forEach(job => {
        const key = dayKey(job.created_at);
        if (key in trendMap) trendMap[key] += 1;
    });
    const jobsTrend = Object.entries(trendMap).map(([date, count]) => ({ date, count }));

    return {
        activeJobs: activeJobs.length,
        completedJobs: jobs.filter(j => j.main_status === 'completed').length,
        cancelledJobs: jobs.filter(j => j.is_cancelled).length,
        urgentJobsCount: activeJobs.filter(j => j.urgent_design || j.urgent_quote).length,
        urgentJobsList: activeJobs.filter(j => j.urgent_design || j.urgent_quote).slice(0, 5),
        thisWeekJobs: jobs.filter(j => new Date(j.created_at) >= weekAgo).length,
        dueThisWeek,
        totalUsers,
        pipelineValue,
        fullyPricedJobs,
        missingPricingJobs,
        notYetQuotedJobs,
        statusBreakdown,
        jobsTrend,
        recentActivity: activity.slice(0, 15),
        recentJobs: jobs.slice(0, 8),
    };
};

export const subscribeToCompanyActivity = (companyId, callback) => {
    if (!companyId) return null;

    return supabase
        .channel(`dashboard-beta-activity-${companyId}`)
        .on(
            'postgres_changes',
            { event: 'INSERT', schema: 'public', table: 'job_action_logs', filter: `company_id=eq.${companyId}` },
            callback
        )
        .subscribe();
};

export const unsubscribeFromCompanyActivity = async channel => {
    if (channel) {
        await supabase.removeChannel(channel);
    }
};
